import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
  Table,
  useQueryState,
  Filters,
  useFilters,
  usePrevious,
  BaseTransaction,
  useGetList,
  SchemaFilterType,
  BaseellaStatus,
  PermissionEnum,
  ButtonPseudo,
  IconDownload,
  ButtonSize,
  SelectItemProps,
  ButtonPrimary,
  IconExpand,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  columns,
  columnsLabels,
  expandableColumns,
  filtersSchema,
  schema,
} from './variables/list';
import {
  useLazyGetInternalListQuery,
  useLazyGetInternalListStatusesQuery,
  useExportInternalTransactionMutation,
} from './store/services';
import { useLazyGetGeneralLedgerTransactionValuesQuery } from '../transactions/store/services';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import formatInternalList from './helpers/format-internal-list';
import {
  clearInternalListState,
  setExpandedTransactionId,
  setSelectedCount,
} from './store/slices/list';
import { setNotificationVisible } from '../../../../../store/slice';

import cn from './styles.module.css';

import ROUTES from '../../routes';

let timeout: NodeJS.Timeout;

const defaultInprogressStatuses = [
  BaseellaStatus.DRAFT,
  BaseellaStatus.NONE,
  BaseellaStatus.NEW,
  BaseellaStatus.HELD,
  BaseellaStatus.SENT,
  BaseellaStatus.PROC,
  BaseellaStatus.ARCH,
  BaseellaStatus.REV,
  BaseellaStatus.AML,
  BaseellaStatus.AML_INFO,
  BaseellaStatus.AML_DONE,
  BaseellaStatus.READY_TO_SEND,
  BaseellaStatus.READY_TO_PSP,
  BaseellaStatus.ACCP_OUT,
];

const InternalList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    root: { userId },
    internal: {
      list: {
        transactions,
        pagination,
        expandedTransactionId,
        collapseAllRows,
        selectedCount,
        errorsList,
      },
    },
    transactionsDictionaries: { currencies, currenciesList, activePermissions },
  } = useAppSelector((state) => state);

  const {
    autocompleteList: accounts,
    onSearch,
    onClear,
  } = useGetAccountsAutocompleteList(
    '&isClientAccount=true',
    'fullNumber',
    true,
  );
  const {
    autocompleteList: senderAccount,
    onSearch: onSenderSearch,
    onClear: onSenderClear,
  } = useGetAccountsAutocompleteList(
    '&isClientAccount=true',
    'fullNumber',
    true,
  );

  const [statusFilterState, setStatusFilterState] = useState<
    'in-progress' | 'unsigned' | 'rejected' | 'all' | undefined
  >(undefined);
  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [onlyRemoved, setOnlyRemoved] = useState(false);

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const { search } = useLocation();
  const navigate = useNavigate();

  const prevTransactions = usePrevious(transactions);
  const prevFilters = usePrevious(filters);

  const [
    exportTransaction,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportInternalTransactionMutation();

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string | number> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor') {
          if (item !== 'transferDate') {
            if (
              item === 'consumerStatus' ||
              item === 'baseellaStatus' ||
              item === 'monitoringStatus' ||
              item === 'benificiaryType' ||
              item === 'chargeType' ||
              item === 'id' ||
              item === 'senderUserId' ||
              item === 'recipientUserId' ||
              item === 'currencyId' ||
              item === 'paymentOrderId' ||
              item === 'typeId' ||
              item === 'excludeAccounts'
            ) {
              exportFilters[item] = filters[item].value;
            } else {
              const [filterValue] = filters[item].value;

              exportFilters[item] = filterValue;
            }
          } else if (item === 'transferDate') {
            const transferDate = filters[item].value;

            const [from, to] = transferDate;

            exportFilters.transferDateFrom = from;

            if (to) {
              exportFilters.transferDateTo = to;
            }
          }
        }
      });
    }

    await exportTransaction(exportFilters);
  };

  const [getTransactionsList, { isLoading, isFetching: internalListFetching }] =
    useLazyGetInternalListQuery();
  const [getInternalStatuses] = useLazyGetInternalListStatusesQuery();

  const [
    getGeneralLedgerTransactionValues,
    { isLoading: glIsLoading, isFetching, isSuccess },
  ] = useLazyGetGeneralLedgerTransactionValuesQuery();

  const getGeneralLedgerAction = (id: string) => {
    dispatch(setExpandedTransactionId(id));

    getGeneralLedgerTransactionValues(id);
  };

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<BaseTransaction>({
      getList: getTransactionsList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    setStatusFilterState(undefined);

    const filterValues: Filters = {
      ...savedFilter,
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  const onInProgressClick = () => {
    setStatusFilterState('in-progress');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: defaultInprogressStatuses,
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');

    onFilter(filterValues);
  };

  const onUnsignedClick = () => {
    setStatusFilterState('unsigned');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [BaseellaStatus.NOT_SIGNED],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');

    onFilter(filterValues);
  };

  const onRejectedClick = () => {
    setStatusFilterState('rejected');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [BaseellaStatus.AML_REJ, BaseellaStatus.REJECTED],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');

    onFilter(filterValues);
  };

  const onAllClick = () => {
    setStatusFilterState('all');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: { value: [], type: SchemaFilterType.STRING },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');

    onFilter(filterValues);
  };

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'recipientAccountNumber') {
      onSearch(searchAutocomplete);
    } else if (name === 'senderAccountNumber') {
      onSenderSearch(searchAutocomplete);
    }
  };

  const onDuplicate = (id: string) => {
    const currentTransaction = transactions?.find((item) => item.id === id);

    if (currentTransaction) {
      navigate(`${ROUTES.INTERNAL_TRANSACTION_CREATE}${search || ''}`, {
        state: { ...currentTransaction, duplicate: true },
      });
    }
  };

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStatuses = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getInternalStatuses(query);
      await onGetStatuses(query);
    }, 5000);
  };

  const onClearAutoComplete = () => {
    onClear();

    setTimeout(() => {
      onSenderClear();
    }, 100);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'internal',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    setValues('recipientAccountNumber', accounts);
    setValues('senderAccountNumber', senderAccount);
    setValues('currencyId', currencies as SelectItemProps[]);
  }, [accounts, currencies, senderAccount]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setSelectedCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      selectedCount &&
      selectedCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !transactions &&
      sort &&
      sort.sortDir
    ) {
      let parsed;

      if (search) {
        parsed = queryString.parse(search);
      }

      let filtersValues: any = {
        take: {
          value: [selectedCount?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      const savedFiltersJSON = localStorage.getItem('internal-filters');

      let parsedSavedFilters: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
      }[];

      let defaultSavedFilter;

      if (!Object.keys(filters)?.length) {
        if (savedFiltersJSON) {
          parsedSavedFilters = JSON.parse(savedFiltersJSON);

          if (parsedSavedFilters && parsedSavedFilters?.length) {
            const currentUserParsedFilters = parsedSavedFilters?.filter(
              (item: any) => item.employeeId === userId,
            );

            if (currentUserParsedFilters && currentUserParsedFilters?.length) {
              defaultSavedFilter = currentUserParsedFilters?.find(
                (item) => item.default,
              );
            }
          }
        }

        if (defaultSavedFilter) {
          setSelectedSavedFilter(defaultSavedFilter.id);

          filtersValues = { ...filtersValues, ...defaultSavedFilter?.value };
        } else {
          filtersValues = { ...filtersValues, ...filters };
        }
      } else {
        filtersValues = { ...filtersValues, ...filters };
      }

      if (!filtersValues.baseellaStatus) {
        filtersValues.baseellaStatus = {
          value: defaultInprogressStatuses,
          type: SchemaFilterType.STRING,
        };

        setStatusFilterState('in-progress');
      } else if (filtersValues?.baseellaStatus?.value && !defaultSavedFilter) {
        const sortedDefaultStatusesInProgress = defaultInprogressStatuses.sort(
          (a, b) => a?.localeCompare(b),
        );

        const sortedValues = filtersValues?.baseellaStatus?.value.sort(
          (a: any, b: any) => a?.localeCompare(b),
        );

        if (isEqual(sortedValues, sortedDefaultStatusesInProgress)) {
          setStatusFilterState('in-progress');
        } else if (isEqual(sortedValues, [BaseellaStatus.NOT_SIGNED])) {
          setStatusFilterState('unsigned');
        } else if (
          isEqual(sortedValues, [
            BaseellaStatus.AML_REJ,
            BaseellaStatus.REJECTED,
          ])
        ) {
          setStatusFilterState('rejected');
        } else {
          setStatusFilterState('all');
        }
      }

      if (parsed && parsed.includeRemoved) {
        setIncludeRemoved(true);

        filtersValues.includeRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      if (parsed && parsed.onlyRemoved) {
        setOnlyRemoved(true);

        filtersValues.onlyRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({ filters: filtersValues, sort });
    }
  }, [filters, sort, transactions, selectedCount, userId]);

  useEffect(() => {
    if (selectedSavedFilter && statusFilterState) {
      setStatusFilterState(undefined);
    }
  }, [selectedSavedFilter, statusFilterState]);

  useEffect(
    () => () => {
      setTimeout(async () => {
        await onClearTimeout();
      });

      dispatch(clearInternalListState());
    },
    [dispatch],
  );

  const formattedData = useMemo(() => {
    const data = formatInternalList({
      list: transactions as BaseTransaction[],
      currenciesList,
    });

    return data;
  }, [transactions, currenciesList]);

  useEffect(() => {
    if (transactions && transactions.length) {
      const ids = transactions.map((item) => item.id);
      const query = `?id=${ids.join('&id=')}`;

      onGetStatuses(query);
    }
  }, [transactions]);

  useEffect(() => {
    if (!isEqual(prevTransactions, transactions) && expandedTransactionId) {
      getGeneralLedgerAction(expandedTransactionId);
    }
  }, [prevTransactions, transactions, expandedTransactionId]);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('internal-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  const content: React.JSX.Element = (
    <Table
      title="Customers internal transfers"
      columns={columns}
      data={formattedData || []}
      onSort={(name) => {
        onSort(name);
        onClearTimeout();
      }}
      filters={filters}
      sort={sort}
      filtersComponent={filtersComponent}
      columnsLabels={columnsLabels}
      onFilterChange={(values) => {
        setSelectedSavedFilter('');

        const filterValues: Filters = {
          ...values,
          take: {
            value: [(selectedCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        if (includeRemoved) {
          filterValues.includeRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        if (onlyRemoved) {
          filterValues.onlyRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilter(filterValues);

        if (!values?.baseellaStatus?.value) {
          setStatusFilterState('all');
        }

        if (
          values.baseellaStatus?.value &&
          filters.baseellaStatus?.value &&
          (!isEqual(
            values.baseellaStatus.value,
            filters.baseellaStatus.value,
          ) ||
            selectedSavedFilter)
        ) {
          const sortedDefaultStatusesInProgress =
            defaultInprogressStatuses.sort((a, b) => a?.localeCompare(b));

          const sortedValues = values.baseellaStatus?.value.sort((a, b) =>
            a?.localeCompare(b),
          );

          if (isEqual(sortedValues, sortedDefaultStatusesInProgress)) {
            setStatusFilterState('in-progress');
          } else if (isEqual(sortedValues, [BaseellaStatus.NOT_SIGNED])) {
            setStatusFilterState('unsigned');
          } else if (
            isEqual(sortedValues, [
              BaseellaStatus.AML_REJ,
              BaseellaStatus.REJECTED,
            ])
          ) {
            setStatusFilterState('rejected');
          } else {
            setStatusFilterState('all');
          }
        }

        onClearTimeout();
      }}
      handleSubmit={handleSubmit}
      isLoading={
        isLoading ||
        internalListFetching ||
        !selectedCount ||
        transactions === undefined
      }
      take={(selectedCount as number)?.toString()}
      setPage={(data) => {
        const filterValues: Filters = {
          ...filters,
          take: {
            value: [(selectedCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        if (includeRemoved) {
          filterValues.includeRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        if (onlyRemoved) {
          filterValues.onlyRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        setPage({ ...data, filters: filterValues });
        onClearTimeout();
      }}
      hiddenFiltersTags={statusFilterState !== 'all' ? ['baseellaStatus'] : []}
      hiddedAllFilterTags={Boolean(selectedSavedFilter)}
      pagination={pagination}
      storeKey="internal-transfers"
      expandableColumns={expandableColumns}
      error={errorsList || errorList}
      onExpand={getGeneralLedgerAction}
      isLoadingExpandableRow={glIsLoading || isFetching}
      paginationDisabled={internalListFetching}
      onClearAutoComplete={onClearAutoComplete}
      collapseAllRows={collapseAllRows}
      isFetching={internalListFetching}
      tableClassName={!transactions?.length ? cn.internalTable : ''}
      tableBodyClassName={cn.internalTableBody}
      filtersContent={
        <div>
          {activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
          ) && (
            <div className={cn.defaultFilters}>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'in-progress' ? cn.active : ''
                }`}
                onClick={() => onInProgressClick()}
              >
                In progress
              </button>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'unsigned' ? cn.active : ''
                }`}
                onClick={() => onUnsignedClick()}
              >
                Not signed
              </button>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'rejected' ? cn.active : ''
                }`}
                onClick={() => onRejectedClick()}
              >
                Rejected
              </button>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'all' ? cn.active : ''
                }`}
                onClick={() => onAllClick()}
              >
                All
              </button>
            </div>
          )}
        </div>
      }
      topContent={
        <div className={cn.tableTopContent}>
          {activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_INTERNAL_CREATE_ACTION,
          ) && (
            <NavLink
              to={`${ROUTES.INTERNAL_TRANSACTION_CREATE}${search || ''}`}
            >
              <ButtonPrimary
                text="Create transaction"
                size={ButtonSize.SMALL}
                icon={<IconExpand />}
              />
            </NavLink>
          )}
          {activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
          ) && (
            <ButtonPseudo
              text={exportIsLoading ? 'Loading...' : 'Export transactions'}
              icon={<IconDownload />}
              className={`button-white ${cn.exportButton}`}
              size={ButtonSize.SMALL}
              onClick={onExportClick}
            />
          )}
        </div>
      }
      onDuplicateClick={(id) => onDuplicate(id)}
      savedFilters={savedFilters}
      setSavedFilters={setSavedFilters}
      savedFiltersKey="internal-filters"
      selectedSavedFilter={selectedSavedFilter}
      onSavedFilterClick={onSavedFilterClick}
      setSelectedSavedFilter={setSelectedSavedFilter}
      isSuccessExpandableRow={isSuccess}
      userId={userId}
      transactionTable
      withVerticalScroll
      expandable
      withSettings
      withScroll
      clickable
      withDuplicateButton
    />
  );

  return (
    <div className={cn.internalWrapper} ref={scrollRef}>
      {content}
    </div>
  );
};

export default InternalList;
